import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Button,
  Breadcrumbs,
  Link as MUILink,
  CircularProgress,
  Grid,
} from "@mui/material";
import { Link } from "react-router-dom";
import { message } from "antd";
import imageCompression from "browser-image-compression";
import { styled } from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useDispatch, useSelector } from "react-redux";
import {
  uploadGalleryImage,
  fetchGalleryImages,
  deleteGalleryImage,
} from "../redux/actions/admin/galleryAction";
import PageTitle from "../Components/PageTitle";

const Gallery = () => {
  const [images, setImages] = useState([]);
  const [imageBase64List, setImageBase64List] = useState([]);

  const dispatch = useDispatch();
  const { loading, images: uploadedImages } = useSelector(
    (state) => state.gallery
  );

  useEffect(() => {
    dispatch(fetchGalleryImages());
  }, [dispatch]);

  const VisuallyHiddenInput = styled("input")({
    display: "none",
  });

  const handleImageUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const maxSizeInMB = 5;
    const base64Images = [];

    try {
      const promises = Array.from(files).map(async (file) => {
        if (file.size / (1024 * 1024) > maxSizeInMB) {
          message.error(`Image size should not exceed ${maxSizeInMB} MB.`);
          return null;
        }

        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 800,
          useWebWorker: true,
        };

        const compressedFile = await imageCompression(file, options);
        const reader = new FileReader();

        return new Promise((resolve) => {
          reader.onloadend = () => {
            let imageBase64 = reader.result;
            if (imageBase64.includes("base64,")) {
              base64Images.push(imageBase64.split("base64,")[1]);
            }
            resolve(imageBase64);
          };
          reader.readAsDataURL(compressedFile);
        });
      });

      const compressedImages = await Promise.all(promises);
      setImages(compressedImages.filter(Boolean));
      setImageBase64List(base64Images);
    } catch (error) {
      console.error("Error compressing images:", error);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (imageBase64List.length === 0) {
      message.error("Please upload at least one image.");
      return;
    }
    dispatch(uploadGalleryImage(imageBase64List)).then(() => {
      setImages([]);
      setImageBase64List([]);
      dispatch(fetchGalleryImages());
    });
  };

  const handleDelete = (publicId) => {
    if (!publicId) return;
    dispatch(deleteGalleryImage(publicId)).then(() => {
      dispatch(fetchGalleryImages());
    });
  };

  return (
    <>
      <PageTitle
        title="Manage Gallery Images - Admin Panel | Shree Mangalam School"
        description="Upload, organize, and manage photo gallery images of school events and activities via the Admin Panel of Shree Mangalam School."
        url="/admin/gallery"
        keywords="admin gallery, photo upload, manage school gallery, Shree Manglam gallery admin, school events images, image manager panel"
      />

      <Typography
        variant="h5"
        align="center"
        gutterBottom
        sx={{
          mb: 3,
          fontFamily: "Poppins, sans-serif",
          color: "#2C3E50",
          letterSpacing: "2.5px",
          lineHeight: 1.8,
        }}
      >
        Gallery Images
      </Typography>

      <Breadcrumbs
        aria-label="breadcrumb"
        sx={{
          mb: 5,
          backgroundColor: "#fff",
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        }}
      >
        <MUILink
          component={Link}
          to="/dashboard"
          sx={{ color: "inherit", textDecoration: "none" }}
        >
          Dashboard
        </MUILink>
        <Typography sx={{ color: "primary.main" }}>Gallery</Typography>
      </Breadcrumbs>

      <Box component="form" onSubmit={handleSubmit}>
        {/* File Upload Section */}
        <VisuallyHiddenInput
          accept="image/*"
          id="contained-button-file"
          type="file"
          multiple
          onChange={handleImageUpload}
        />
        <label htmlFor="contained-button-file">
          <Button
            variant="outlined"
            color="primary"
            component="span"
            startIcon={<CloudUploadIcon />}
            sx={{
              mb: 3,
              textTransform: "none",
              fontFamily: "Poppins, sans-serif",
              fontWeight: 500,
            }}
          >
            Upload Gallery Images
          </Button>
        </label>

        {/* Preview Uploaded Images */}
        {images.length > 0 && (
          <Grid container spacing={2} mb={3}>
            {images.map((image, index) => (
              <Grid item key={index} xs={6} sm={4} md={3}>
                <Box
                  sx={{
                    position: "relative",
                    borderRadius: "8px",
                    overflow: "hidden",
                    boxShadow: 2,
                  }}
                >
                  <img
                    src={image}
                    alt={`banner-preview-${index}`}
                    style={{
                      height: "150px",
                      width: "100%",
                      objectFit: "cover",
                    }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        )}

        <br />

        {/* Submit Button */}
        <Button
          variant="contained"
          type="submit"
          disabled={loading}
          sx={{
            mb: 5,
            textTransform: "none",
            fontFamily: "Poppins, sans-serif",
            fontWeight: 500,
          }}
        >
          {loading ? <CircularProgress size={24} /> : "Add Gallery Images"}
        </Button>
        <hr />
        {/* Display All Uploaded Images */}
        <Typography
          variant="h5"
          sx={{
            pb: 2,
            pt: 3,
            fontFamily: "Poppins, sans-serif",
            color: "#555",
            letterSpacing: "2.5px",
          }}
        >
          All Gallery Images
        </Typography>

        <Grid container spacing={3} pt={1} mb={5}>
          {uploadedImages &&
            uploadedImages.map((image, index) => (
              <Grid item key={index} xs={12} sm={6} md={4}>
                <Box
                  sx={{
                    position: "relative",
                    borderRadius: "8px",
                    overflow: "hidden",
                    boxShadow: 3,
                    transition: "transform 0.3s ease-in-out",
                    "&:hover": {
                      transform: "scale(1.02)",
                    },
                  }}
                >
                  <img
                    src={image.imageUrl}
                    alt={`uploaded-banner-${index}`}
                    style={{
                      height: "250px",
                      width: "100%",
                      objectFit: "cover",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      right: 0,
                      p: 1,
                      bgcolor: "rgba(0, 0, 0, 0.5)",
                    }}
                  >
                    <Button
                      variant="contained"
                      color="error"
                      size="small"
                      onClick={() => handleDelete(image.publicId)}
                      sx={{
                        textTransform: "none",
                        fontFamily: "Poppins, sans-serif",
                        fontWeight: 500,
                      }}
                    >
                      {loading ? <CircularProgress size={20} /> : "Delete"}
                    </Button>
                  </Box>
                </Box>
              </Grid>
            ))}
        </Grid>
      </Box>
    </>
  );
};

export default Gallery;
